import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import * as _ from 'lodash';
import { HttpService } from 'app/shared/services/http.service';
import { GridService } from 'app/shared/services/grid.service';
import { SharedService } from 'app/shared/services/shared.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { ModalsService } from 'app/shared/services/modals.service';
import { UserService } from 'app/shared/services/user.service';
import { GoogleAnalyticsService } from 'app/shared/services/google-analytics.service';

@AutoUnsubscribe()
@Component({
  selector: 'manage-supply-component',
  templateUrl: './manage-supply.component.html',
  styleUrls: ['./manage-supply.component.scss'],
})
export class ManageSupplyComponent implements OnInit {
  public data: any;
  public columnsSettings: Array<any>;
  public isLoading: boolean;
  public isError: boolean;
  public emptyDataSettings: any;
  public activeEntities: boolean;
  public isInactiveLoaded: boolean;
  public withAmount: boolean;
  public gridName: string;
  public gridFilters: any;
  public getGridObservable: any;
  public subscriptions: Array<any>;

  constructor(
    public httpService: HttpService,
    public gridService: GridService,
    public sharedService: SharedService,
    public dictionaryService: DictionaryService,
    public modalsService: ModalsService,
    public userService: UserService,
    public ga: GoogleAnalyticsService,
    public router: Router
  ) {
    this.data = [];
    this.columnsSettings = [];
    this.isLoading = false;
    this.isError = false;
    this.emptyDataSettings = {
      title: 'WELCOME_TO_CEDATO',
      info: 'MANAGE_SUPPLY_NO_DATA',
      buttonText: 'CREATE_NEW',
      buttonLink: '/supply/new',
    };
    this.activeEntities = true;
    this.withAmount = false;
    this.isInactiveLoaded = false;
    this.gridName = 'MANAGE_SUPPLY';
    this.gridFilters = {
      $$active: [this.activeEntities],
    };
    this.subscriptions = [];
  }

  ngOnDestroy() {}

  ngOnInit() {
    this.initialize();
    this.subscriptions.push(
      this.sharedService.getMessagesBus().subscribe((response: any) => {
        if (response.type === 'NETWORK_CHANGE') {
          this.activeEntities = true;
          this.isInactiveLoaded = false;
          this.gridFilters = {
            $$active: [this.activeEntities],
          };
          this.initialize();
        } else if (response.type === 'GRID_VALUE_CHANGE') {
          this.onActiveClick(response.data);
        }
      })
    );
    this.sharedService.setPageTitle(this.gridName);
  }

  initialize() {
    this.buildColumns();
    this.getSupplies();
  }

  buildColumns() {
    this.columnsSettings = [
      {
        key: '$$active',
        label: 'ACTIVE',
        parseTooltipFunction: this.gridService.parseActiveSupplyTooltip,
        isActiveButton: true,
      },
      { key: 'si', label: 'SUPPLY_ID', width: 200, filterType: 'INPUT', primaryKey: true },
      { key: 'sn', label: 'SUPPLY_NAME', width: 200, filterType: 'INPUT' },
      { key: 'sp', label: 'PARTNER_NAME', filterType: 'SELECT' },
      {
        key: 'st',
        label: 'TYPE',
        filterType: 'SELECT',
        parseFunction: this.gridService.parseSupplyType,
        parseTooltipFunction: this.gridService.parseSupplyType,
      },
      {
        key: 'dc',
        label: 'CAMPAIGN',
        tooltipKey: 'CAMPAIGN_TOOLTIP',
        filterType: 'SELECT',
        width: 84,
        parseFunction: this.gridService.parseBoolean,
        parseTooltipFunction: this.gridService.parseBooleanTooltip,
        featureFlag: 'manage_campaigns',
      },
      {
        key: 'cr',
        label: 'RUNNING_CAMPAIGN',
        tooltipKey: 'RUNNING_CAMPAIGN_TOOLTIP',
        filterType: 'SELECT',
        width: 84,
        parseFunction: this.gridService.parseBoolean,
        parseTooltipFunction: this.gridService.parseBooleanTooltip,
        featureFlag: 'manage_campaigns',
      },
      {
        key: 'sb',
        label: 'SUPPLY_SUB_TYPE',
        filterType: 'SELECT',
        parseFunction: this.gridService.parseSupplySubType,
        parseTooltipFunction: this.gridService.parseSupplySubType,
        defaultVisibility: false,
      },
    ];

    if (this.userService.isCedatoPublisherNetwork() || this.userService.getFeatureState('hb_type')) {
      this.columnsSettings = [
        ...this.columnsSettings,
        ...[
          {
            key: 'hb',
            label: 'HB_TYPE',
            filterType: 'SELECT',
            parseFunction: this.gridService.parseBooleanTooltip,
            parseTooltipFunction: this.gridService.parseBooleanTooltip,
          },
        ],
      ];
    }

    this.columnsSettings = [
      ...this.columnsSettings,
      ...[
        {
          key: 'sv',
          label: 'ASSIGNED_DEMAND',
          width: 200,
          parseFunction: this.gridService.parseNumeric,
          parseTooltipFunction: this.gridService.parseNumeric,
          defaultVisibility: false,
        },
        {
          key: 'ev',
          label: 'ENVIRONMENT_SHORT',
          filterType: 'SELECT',
          parseFunction: this.gridService.parseMultipleEnvironments,
          parseTooltipFunction: this.gridService.parseMultipleEnvironmentsTooltip,
        },
        {
          key: 'la',
          label: 'LIMITED_ASSIGNMENT',
          filterType: 'SELECT',
          parseFunction: this.gridService.parseBooleanTooltip,
          parseTooltipFunction: this.gridService.parseBooleanTooltip,
          featureFlag: 'limit_assign',
        },
      ],
    ];

    if (this.userService.isAdmin()) {
      this.columnsSettings.push({
        key: 'ep',
        label: 'EXTRA_PARAMS',
        filterType: 'SELECT',
        parseFunction: this.gridService.parseBoolean,
        parseTooltipFunction: this.gridService.parseBooleanTooltip,
      });
    }
  }

  getSupplies() {
    if (this.getGridObservable) {
      this.getGridObservable.unsubscribe();
    }

    const customSettings: any = JSON.parse(localStorage.getItem('customGridSettings'));
    if (customSettings[this.gridName] !== undefined) {
      if (customSettings[this.gridName]['visibleColumns'].length && !this.withAmount) {
        this.withAmount = customSettings[this.gridName]['visibleColumns'].includes('sv');
      }
    }

    const params: any = {};
    params.manage = 1;
    params.withAmount = this.withAmount ? 1 : 0;

    if (this.userService.getFeatureState('limit_assign')) {
      params.limit_assign = 1;
    }

    if (this.userService.getFeatureState('hb_type') || this.userService.isCedatoPublisherNetwork()) {
      params.hb_type = 1;
    }

    if (this.userService.getFeatureState('manage_campaigns')) {
      params.manage_campaigns = 1;
    }

    if (this.gridFilters.$$active && this.gridFilters.$$active.length === 1) {
      params.activeSupplies = this.gridFilters.$$active[0] ? 1 : 0;
    }
    this.isLoading = true;

    this.getGridObservable = this.httpService.post('vdsp_ws/getSupplies', params).subscribe(
      (response: any) => {
        let one: any;
        const data: Array<any> = [];
        for (let i = 0; i < response.length; i++) {
          one = {};
          one.si = +response[i].si;
          one.sn = response[i].sn;
          one.sp = response[i].sp;
          one.sb = +response[i].sb;
          one.ep = +response[i].ep;

          if (!this.sharedService.parseBoolean(response[i].sa) && this.activeEntities) {
            this.activeEntities = false;
            this.gridFilters.$$active = [true, false];
          }
          one.ev = {
            is_desktop: response[i].ed === '1',
            is_mobile_web: response[i].ew === '1',
            is_mobile_app: response[i].ea === '1',
            is_ctv: response[i].ec === '1',
          };
          if (!this.sharedService.isEmpty(response[i].parentNetworkName)) {
            one.sp = response[i].parentNetworkName;
          }
          one.st = parseInt(response[i].st);

          if (this.userService.networkData && this.userService.networkData.mpnid) {
            if (this.userService.networkData.mpnid.includes(+response[i].parentNetworkId)) {
              one.st = this.dictionaryService.get('CEDATOX_MARKETPLACE');
            }
          }

          // set Supply Sub Type as empty - for DAS-4670 issue.
          if (+response[i].st !== 0) {
            one.sb = 2;
          }

          // FeatureFlagged Columns
          if (params.manage_campaigns === 1) {
            one.dc = this.sharedService.parseBoolean(response[i].dc);
            one.cr = this.sharedService.parseBoolean(response[i].cr);
          }

          if (params.limit_assign === 1) {
            one.la = this.sharedService.parseBoolean(response[i].la);
          }

          if (params.hb_type === 1) {
            one.hb = this.sharedService.parseBoolean(response[i].hb);
          }

          if (this.withAmount) {
            one.sv = +response[i].sv;
          }

          // Special Columns
          one.$$active = this.sharedService.parseBoolean(response[i].sa);
          one.$$actions = [
            {
              type: 'EDIT',
              link: true,
            },
          ];
          if (response[i].st !== 2 || this.userService.getFeatureState('duplicate_cedatox')) {
            // // if (((+response[i].sb == 1) && this.userService.getFeatureState('subtype_content_unit') && this.userService.isAdmin())
            // //   || +response[i].sb == 0)
            {
              one.$$actions.push({
                type: 'COPY',
                link: false,
                linkOverride: '/supply/manage/',
              });
            }
          }
          one.$$link = '/supply/manage/' + response[i].si;
          data.push(one);
        }

        this.data = data;
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
        this.isError = true;
      }
    );
  }

  onActiveClick(data: any) {
    const playerId: number = parseInt(data.dataItem.si);
    const active: boolean = !data.dataItem.$$active;
    const updatedData: Array<any> = _.cloneDeep(this.data);
    const itemIndex: number = _.findIndex(updatedData, { si: playerId });
    const params = {
      playerId,
      is_active: active,
      no_return: true,
      name: data.dataItem.sn,
    };
    this.gridFilters = data.filers;
    if (itemIndex > -1) {
      this.isLoading = true;
      this.subscriptions.push(
        this.httpService.post('vdsp_ws/saveSupply', params).subscribe((response: any) => {
          this.isLoading = false;
          if (response && response.status && response.status.toLowerCase() === 'ok') {
            updatedData[itemIndex].$$active = active;
            this.data = updatedData;
            this.userService.networkData.active_supplies_limit_reached = response.limit_reached;
            this.sharedService.showFlashMessage(this.dictionaryService.get('UPDATED_SUCCESSFULLY'), 'success');
          } else {
            if (response && response.errors && response.errors.active_supplies_limit === 'NOT_VALID_BY_CLIENT') {
              this.sharedService.modalCedatoState = true;
              this.sharedService.modalCedatoHeader = 'WARNING';
              this.sharedService.modalCedatoBody = this.dictionaryService.get('LIMITS_NO_CREATE_MSG')('Supply');
              this.sharedService.modalCedatoButton = 'OK';
            } else {
              this.sharedService.showErrorFlashMessage();
            }
          }
        })
      );
    } else {
      this.sharedService.showErrorFlashMessage();
    }
  }

  onFilterChange($event: any) {
    if ($event.$$active) {
      this.gridFilters = $event;
      if (($event.$$active.length > 1 || !$event.$$active[0]) && !this.isInactiveLoaded) {
        this.activeEntities = false;
        this.isInactiveLoaded = true;
        this.getSupplies();
      }
    }
  }

  onColumnsVisibilityChange($event: any) {
    const find: any = _.find($event, { key: 'sv' });
    this.columnsSettings = _.cloneDeep($event);

    if (find.visible && !this.withAmount) {
      this.withAmount = find.visible;
      this.getSupplies();
    }
  }

  createNewSupplyBtnClick() {
    if (this.userService.networkData.active_supplies_limit_reached) {
      this.sharedService.modalCedatoState = true;
      this.sharedService.modalCedatoHeader = 'WARNING';
      this.sharedService.modalCedatoBody = this.dictionaryService.get('LIMITS_NO_CREATE_MSG')('Supply');
      this.sharedService.modalCedatoButton = 'OK';
    } else {
      this.router.navigate(['/supply/new']).then(() => {
        return;
      });
    }
  }

  duplicateSupplyBtnClick($event: any) {
    if (this.userService.networkData.active_supplies_limit_reached) {
      this.sharedService.modalCedatoState = true;
      this.sharedService.modalCedatoHeader = 'WARNING';
      this.sharedService.modalCedatoBody = this.dictionaryService.get('LIMITS_NO_CREATE_MSG')('Supply');
      this.sharedService.modalCedatoButton = 'OK';
    } else {
      if ($event && $event.si) {
        this.router.navigate(['/supply/duplicate/' + $event.si]).then(() => {
          return;
        });
      } else {
        this.router.navigate(['/supply/new']).then(() => {
          return;
        });
      }
    }
  }
}
